import { Box, Paper, Typography, useTheme, Chip, Stack, Button } from "@mui/material";
import { CohortMemberStatus, CohortStatus } from "generated-graphql/graphql";
import { useCohortDetails } from "./useCohortDetails";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryParams } from "hooks/useQueryParams";
import { useResetSafetyOverrideAuth } from "hooks/useResetReportAuth";
import { SplitCohortDialog } from "../../../components/Cohort/SplitCohortDialog";
import { SplitCohortButton } from "../../../components/Cohort/SplitCohortButton";
import { RecoverStrandedMembersDialog } from "../../../components/Cohort/RecoverStrandedMembersDialog";
import { useState } from "react";

// Type for cohort member from the GraphQL query
type CohortMember = {
  id: string;
  status: CohortMemberStatus;
  facility?: {
    id: string;
    name: string;
    city?: string | null;
    state?: string | null;
    tenant?: {
      id: string;
      name: string;
    } | null;
  } | null;
};

interface StatusSummaryItemProps {
  status: CohortMemberStatus;
  count: number;
  onClick: (event: React.MouseEvent) => void;
  isActive: boolean;
}

function StatusSummaryItem({
  status,
  count,
  onClick,
  isActive,
}: StatusSummaryItemProps) {
  const theme = useTheme();

  const getStatusColor = (status: CohortMemberStatus) => {
    switch (status) {
      case CohortMemberStatus.Ready:
        return theme.palette.success.main;
      case CohortMemberStatus.NotReady:
        return theme.palette.error.main;
      case CohortMemberStatus.NotParticipating:
        return theme.palette.warning.main;
      case CohortMemberStatus.Unknown:
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const formatStatus = (status: CohortMemberStatus) => {
    return status.replace(/_/g, " ");
  };

  return (
    <Chip
      label={`${count} ${formatStatus(status)}`}
      onClick={onClick}
      variant={isActive ? "outlined" : "filled"}
      sx={{
        backgroundColor: isActive ? "transparent" : getStatusColor(status),
        color: isActive
          ? getStatusColor(status)
          : theme.palette.getContrastText(getStatusColor(status)),
        borderColor: getStatusColor(status),
        borderWidth: isActive ? 2 : 0,
        cursor: "pointer",
        fontWeight: isActive ? 600 : 400,
        "&:hover": {
          backgroundColor: isActive
            ? `${getStatusColor(status)}20` // Light tint when active (outlined)
            : getStatusColor(status),
          color: isActive
            ? getStatusColor(status)
            : theme.palette.getContrastText(getStatusColor(status)),
          opacity: isActive ? 1 : 0.85,
        },
      }}
    />
  );
}

export function CohortMemberSummary() {
  const theme = useTheme();
  const { data, refetch } = useCohortDetails();
  const navigate = useNavigate();
  const { cohortId } = useParams();
  const { filters } = useQueryParams<{ omnisearch?: string }>();
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [recoverDialogOpen, setRecoverDialogOpen] = useState(false);
  const { canOverrideSafetyCheck: canCreateRecoveryCohort } =
    useResetSafetyOverrideAuth();

  const cohort = data?.cohort;

  if (!cohort || !cohort.members) {
    return null;
  }

  // Get the current status filter from omnisearch
  const currentOmnisearch = filters.omnisearch || "";
  const getActiveStatus = () => {
    // Parse the omnisearch to find status filter
    const statusMatch = currentOmnisearch.match(/status:(\S+)/);
    return statusMatch ? statusMatch[1] : null;
  };
  const activeStatus = getActiveStatus();

  // Calculate member status counts
  const statusCounts = cohort.members.reduce(
    (acc: Record<string, number>, member: CohortMember) => {
      const status = member.status;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const handleStatusClick =
    (status: CohortMemberStatus) => (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const url = new URL(window.location.href);

      // If this status is already active, clear the filter
      if (activeStatus === status) {
        url.searchParams.delete("omnisearch");
      } else {
        // Otherwise, set the new status filter
        const omnisearchParam = `status:${status}`;
        url.searchParams.set("omnisearch", omnisearchParam);
      }

      // Navigate to the same path with updated search params
      navigate(url.pathname + "?" + url.searchParams.toString(), {
        replace: true,
        preventScrollReset: true,
      });
    };

  // Get status entries sorted by count (descending)
  const statusEntries = Object.entries(statusCounts)
    .map(([status, count]) => ({
      status: status as CohortMemberStatus,
      count: count as number,
    }))
    .sort((a, b) => b.count - a.count);

  // Determine if split button should be shown
  const shouldShowSplitButton = cohort.status === CohortStatus.Open;

  const handleSplitCohort = () => {
    setSplitDialogOpen(true);
  };

  const handleSplitSuccess = () => {
    refetch();
  };

  return (
    <Paper
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        p: 2,
      }}
      elevation={0}
    >
      <Typography
        variant="h6"
        fontWeight="600"
        sx={{ mb: 1.5, color: theme.palette.text.primary }}
      >
        Cohort Member Summary
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            mb: 1,
          }}
        >
          Total Members: {cohort.members.length}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {statusEntries.map(({ status, count }) => (
            <StatusSummaryItem
              key={status}
              status={status}
              count={count}
              onClick={handleStatusClick(status)}
              isActive={activeStatus === status}
            />
          ))}
        </Stack>
      </Box>

      {shouldShowSplitButton && (
        <Box
          sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mb: 1.5,
            }}
          >
            Split this cohort to separate ready members from those still in
            progress. This will close the current cohort with ready members and
            create a new open cohort for the remaining members.
          </Typography>
          <SplitCohortButton
            onClick={handleSplitCohort}
            cohortStatus={cohort.status}
            members={cohort.members}
          />
        </Box>
      )}

      {cohort.status === CohortStatus.Closed &&
        cohort.allowsMultipleCohorts === false &&
        canCreateRecoveryCohort && (
        <Box
          sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mb: 1.5,
            }}
          >
            If this closed cohort has stranded members due to late arrival
            rules, create a recovery cohort to continue processing those
            members.
          </Typography>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={() => setRecoverDialogOpen(true)}
          >
            Create Recovery Cohort
          </Button>
        </Box>
      )}

      <SplitCohortDialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        cohortId={cohortId!}
        members={cohort.members.map((member) => ({
          id: member.id,
          status: member.status,
        }))}
        onSuccess={handleSplitSuccess}
      />

      {cohortId && (
        <RecoverStrandedMembersDialog
          open={recoverDialogOpen}
          onClose={() => setRecoverDialogOpen(false)}
          sourceCohortId={cohortId}
          canExecuteRecovery={canCreateRecoveryCohort}
          onSuccess={(newCohortId) => {
            navigate(`/staff/cohorts/${newCohortId}/activities`);
          }}
        />
      )}
    </Paper>
  );
}
